import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import HeroNewsletterForm from "@/components/resources/HeroNewsletterForm";
import { useQueries, useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Loader2, Filter, X } from "lucide-react";
import SEOHead from "@/components/SEOHead";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import KnowledgeHeroAnimation from "@/components/KnowledgeHeroAnimation";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import ResourceCard from "@/components/resources/ResourceCard";
import ResourceFilterSidebar from "@/components/resources/ResourceFilterSidebar";
import { FILTER_DISPLAY_NAMES } from "@/components/blog/BlogFilterSidebar";
import { expandFilterAliases } from "@/lib/filterAliases";
import BlogSearch from "@/components/blog/BlogSearch";
import BlogCardSkeleton from "@/components/blog/BlogCardSkeleton";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { CONTENT_TYPES, KNOWLEDGE_CENTER_TYPES, EXCLUDED_TAGS, EXCLUDED_FILTER_CATEGORIES, getContentTypeLabel, type ContentTypeKey, type UnifiedResource } from "@/lib/resourceTypes";
import { uiT } from "@/i18n/ui";
import { useLanguage } from "@/hooks/useLanguage";
import { getKCT } from "@/i18n/knowledgeCenter";
import { localizeHref } from "@/lib/localizeHref";
import { localizeContentRow } from "@/lib/localizeContent";

const SHARED_COLUMNS = "id, slug, title, summary, featured_image, category, industry_category, service_category, published_at, author, tags, content" as const;
const BLOG_COLUMNS = `${SHARED_COLUMNS}, lang, translation_of` as const;
const LOCALIZED_COLUMNS = ", title_es, summary_es, content_es, slug_es" as const;
// Tables that have Simplified Chinese translation columns
const ZH_TRANSLATED_TABLES = new Set(["case_studies", "white_papers", "checklists"]);
const ZH_LOCALIZED_COLUMNS = ", title_zh, summary_zh, content_zh, slug_zh" as const;

const Resources = () => {
  const lang = useLanguage();
  const t = uiT(lang);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [selectedFormats, setSelectedFormats] = useState<ContentTypeKey[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [visibleCount, setVisibleCount] = useState(18);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Parallel queries for all content types
  const tableQueries = useQueries({
    queries: KNOWLEDGE_CENTER_TYPES.map((ct) => ({
      queryKey: ["resources", ct.key, lang],
      queryFn: async () => {
        const isBlog = ct.key === "blog_posts";
        const hasZh = ZH_TRANSLATED_TABLES.has(ct.key);
        const localizedCols = hasZh ? `${LOCALIZED_COLUMNS}${ZH_LOCALIZED_COLUMNS}` : LOCALIZED_COLUMNS;
        const base = isBlog ? BLOG_COLUMNS : `${SHARED_COLUMNS}${localizedCols}`;
        const columns = ct.key === "webinars" ? `${base}, webinar_date, recording_url, parent_slug, lang` : ct.key === "podcasts" ? `${base}, video_url, episode_number` : base;

        const { data, error } = await supabase
          .from(ct.key as any)
          .select(columns)
          .not("published_at", "is", null)
          .order("published_at", { ascending: false });
        if (error) throw error;

        let rows = (data ?? []) as any[];

        // For blog_posts, deduplicate: prefer translated version, fall back to EN
        if (ct.key === "blog_posts" && lang !== "en") {
          const targetLang = lang === "es" ? "es" : "zh-cn";
          if (lang === "zh-cn") {
            // Strict: only show posts already translated to Chinese
            rows = rows.filter((r: any) => r.lang === "zh-cn");
          } else {
            const translatedIds = new Set(
              rows.filter((r: any) => r.lang === targetLang && r.translation_of)
                .map((r: any) => r.translation_of)
            );
            // Keep: translated posts in target lang + EN posts that have no translation
            rows = rows.filter((r: any) =>
              r.lang === targetLang || (r.lang === "en" && !translatedIds.has(r.id))
            );
          }
        } else if (ct.key === "blog_posts") {
          // EN page: only show English posts
          rows = rows.filter((r: any) => r.lang === "en");
        }

        // Webinars also have a lang column — filter strictly by current language (EN/ES)
        if (ct.key === "webinars" && lang !== "zh-cn") {
          rows = rows.filter((r: any) => r.lang === lang);
        }

        // For zh-cn on tables with ZH columns: require an actual Chinese translation
        if (lang === "zh-cn" && hasZh) {
          rows = rows.filter((r: any) => r.title_zh && String(r.title_zh).trim().length > 0);
        }

        // For zh-cn on tables without any ZH translation columns: hide entirely
        if (lang === "zh-cn" && !hasZh && !isBlog) {
          rows = [];
        }

        return rows.map((row: any) => ({ ...localizeContentRow(row, lang), contentType: ct.key } as UnifiedResource));
      },
    })),
  });

  const isLoading = tableQueries.some((q) => q.isLoading);

  // Merge, sort, and filter
  const allResources = useMemo(() => {
    const merged: UnifiedResource[] = [];
    for (const q of tableQueries) {
      if (q.data) merged.push(...q.data);
    }
    // Exclude on-demand webinar pages (they have parent_slug set)
    const withoutOnDemand = merged.filter((r) => !(r.contentType === "webinars" && r.parent_slug));
    // Exclude content tagged with Business, Communications, etc.
    const filtered = withoutOnDemand.filter((r) => {
      const tags = r.tags ?? [];
      const cat = r.category ?? "";
      return !EXCLUDED_TAGS.some(
        (ex) => cat.toLowerCase() === ex.toLowerCase() || tags.some((t) => t.toLowerCase() === ex.toLowerCase())
      );
    });
    // Upcoming webinars (future webinar_date) pinned to top, then by published_at desc
    const now = Date.now();
    filtered.sort((a, b) => {
      const aUpcoming = a.contentType === "webinars" && a.webinar_date && new Date(a.webinar_date).getTime() > now ? 1 : 0;
      const bUpcoming = b.contentType === "webinars" && b.webinar_date && new Date(b.webinar_date).getTime() > now ? 1 : 0;
      if (aUpcoming !== bUpcoming) return bUpcoming - aUpcoming;
      const da = (a.contentType === "webinars" && a.webinar_date ? new Date(a.webinar_date).getTime() : 0) || (a.published_at ? new Date(a.published_at).getTime() : 0);
      const db = (b.contentType === "webinars" && b.webinar_date ? new Date(b.webinar_date).getTime() : 0) || (b.published_at ? new Date(b.published_at).getTime() : 0);
      return db - da;
    });
    return filtered;
  }, [tableQueries.map((q) => q.dataUpdatedAt).join(",")]);

  const filteredResources = useMemo(() => {
    let list = allResources;

    // Format filter
    if (selectedFormats.length > 0) {
      list = list.filter((r) => selectedFormats.includes(r.contentType));
    }

    // Tag/category filter — AND logic: resource must match every selected filter
    if (selectedFilters.length > 0) {
      list = list.filter((r) => {
        const rCat = (r.category ?? "").toLowerCase();
        const rIndustry = (r.industry_category ?? "").toLowerCase();
        const rService = (r.service_category ?? "").toLowerCase();
        const rTags = (r.tags ?? []).map((t) => t.toLowerCase());
        return selectedFilters.every((filter) => {
          const aliases = expandFilterAliases([filter]).map((a) => a.toLowerCase());
          return aliases.some((a) => rCat === a || rIndustry === a || rService === a || rTags.includes(a));
        });
      });
    }

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          (r.summary ?? "").toLowerCase().includes(q)
      );
    }

    return list;
  }, [allResources, selectedFormats, selectedFilters, searchQuery]);

  const visibleResources = filteredResources.slice(0, visibleCount);
  const hasMore = visibleCount < filteredResources.length;

  // Collect available tag filters
  const availableFilters = useMemo(() => {
    const set = new Set<string>();
    const excludedLower = [...EXCLUDED_TAGS, ...EXCLUDED_FILTER_CATEGORIES].map((e) => e.toLowerCase());
    for (const r of allResources) {
      if (r.category && !excludedLower.includes(r.category.toLowerCase())) set.add(r.category);
      if (r.industry_category && !excludedLower.includes(r.industry_category.toLowerCase())) set.add(r.industry_category);
      if (r.service_category && !excludedLower.includes(r.service_category.toLowerCase())) set.add(r.service_category);
      for (const t of r.tags ?? []) {
        if (!excludedLower.includes(t.toLowerCase())) set.add(t);
      }
    }
    return [...set].sort();
  }, [allResources]);

  // Infinite scroll using IntersectionObserver with cooldown to prevent scroll jumps
  const loadCooldownRef = useRef(false);
  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || !hasMore) return;

    // Prevent rapid re-triggering that causes scroll jumps
    if (loadCooldownRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          loadCooldownRef.current = true;
          setVisibleCount((c) => c + 18);
          // Allow next observation after a short cooldown
          setTimeout(() => { loadCooldownRef.current = false; }, 300);
        }
      },
      { threshold: 0, rootMargin: "200px" }
    );

    // Delay observation to let layout settle after adding new items
    const timer = setTimeout(() => {
      if (loadMoreRef.current) {
        observer.observe(loadMoreRef.current);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [hasMore, visibleCount, filteredResources.length]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(18);
  }, [selectedFilters, selectedFormats, searchQuery]);

  const handleSearch = useCallback((q: string) => setSearchQuery(q), []);

  const activeFilterCount = selectedFilters.length + selectedFormats.length;

  const res = getKCT("resources", lang);

  return (
    <div className="min-h-screen">
      <ScrollToTop />
      <Navbar />
      <SEOHead
        title={res.title}
        description={res.description}
        canonical={`https://qbdgroup.com${localizeHref("/en/knowledge-center", lang)}`}
      />
      <BreadcrumbJsonLd items={[{ name: res.breadcrumbPage }]} />
      {/* Hero */}
      <section className="relative pt-36 pb-20 sm:pb-28 bg-primary overflow-hidden">
        <KnowledgeHeroAnimation />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-accent/5 -translate-y-1/2 translate-x-1/3 blur-3xl pointer-events-none" />
        <div className="relative z-10 container mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <nav aria-label="Breadcrumb" className="mb-8 mt-4">
              <ol className="flex items-center gap-2 text-xs text-primary-foreground/40 tracking-wide uppercase">
                <li><a href={localizeHref("/", lang)} className="hover:text-primary-foreground/70 transition-colors">{t("home")}</a></li>
                <li aria-hidden="true" className="text-primary-foreground/20">/</li>
                <li className="text-primary-foreground/60">{res.breadcrumbPage}</li>
              </ol>
            </nav>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground tracking-tight leading-[1.1]">
              {res.heroTitle}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-primary-foreground/60 max-w-xl leading-relaxed">
              {res.heroDesc}
            </p>
            <HeroNewsletterForm />
            <p className="mt-6">
              <a
                href={localizeHref("/en/knowledge-center/glossary", lang)}
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary-foreground/80 underline decoration-primary-foreground/30 underline-offset-4 hover:text-primary-foreground transition-colors"
              >
                {lang === "es"
                  ? "Glosario regulatorio de Life Sciences"
                  : lang === "zh-cn"
                    ? "生命科学法规术语表"
                    : "Life Sciences regulatory glossary"}
              </a>
            </p>

          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-qbd-ice">
        <div className="container mx-auto px-6 lg:px-10">
          {/* Top bar */}
          <div className="mb-6 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                className="lg:hidden gap-2"
                onClick={() => setShowMobileFilters(!showMobileFilters)}
              >
                <Filter size={14} />
                {t("filters")}
                {activeFilterCount > 0 && (
                  <span className="ml-1 text-xs bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
              {selectedFormats.map((f) => {
                const ct = CONTENT_TYPES.find((c) => c.key === f)!;
                return (
                  <button
                    key={f}
                    onClick={() => setSelectedFormats(selectedFormats.filter((x) => x !== f))}
                    className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full hover:opacity-80 transition-opacity text-white"
                    style={{ backgroundColor: ct.color }}
                  >
                    {getContentTypeLabel(f, lang)} <X size={12} />
                  </button>
                );
              })}
              {selectedFilters.map((f) => (
                <button
                  key={f}
                  onClick={() => setSelectedFilters(selectedFilters.filter((x) => x !== f))}
                  className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium bg-primary/8 text-primary rounded-full hover:bg-primary/15 transition-colors"
                >
                  {FILTER_DISPLAY_NAMES[f] ?? f} <X size={12} />
                </button>
              ))}
              {activeFilterCount > 1 && (
                <button
                  onClick={() => { setSelectedFilters([]); setSelectedFormats([]); }}
                  className="inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t("clearAllFilters")}
                </button>
              )}
            </div>
            <span className="text-sm text-muted-foreground shrink-0">
              {filteredResources.length > 0 && `${filteredResources.length} ${t("resources")}`}
            </span>
          </div>

          {/* Mobile filters */}
          {showMobileFilters && (
            <div className="lg:hidden mb-6 bg-background border border-border rounded-xl p-4">
              <ResourceFilterSidebar
                selectedFilters={selectedFilters}
                onFilterChange={(f) => { setSelectedFilters(f); setShowMobileFilters(false); }}
                availableFilters={availableFilters}
                selectedFormats={selectedFormats}
                onFormatChange={(f) => { setSelectedFormats(f); setShowMobileFilters(false); }}
              />
            </div>
          )}

          <div className="flex gap-10">
            {/* Desktop sidebar */}
            <div className="hidden lg:block w-64 shrink-0">
              <div className="sticky top-36 space-y-6 max-h-[calc(100vh-10rem)] overflow-y-auto pr-1">
                <BlogSearch onSearch={handleSearch} placeholder={t("searchArticles")} />
                <ResourceFilterSidebar
                  selectedFilters={selectedFilters}
                  onFilterChange={setSelectedFilters}
                  availableFilters={availableFilters}
                  selectedFormats={selectedFormats}
                  onFormatChange={setSelectedFormats}
                />
              </div>
            </div>

            {/* Main content */}
            <main className="flex-1 min-w-0">
              {isLoading ? (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <BlogCardSkeleton key={i} />
                  ))}
                </div>
              ) : visibleResources.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-muted-foreground text-lg mb-2">
                    {searchQuery
                      ? `${t("noResultsFor")} "${searchQuery}".`
                      : activeFilterCount > 0
                      ? t("noResourcesFilters")
                      : t("noResourcesYet")}
                  </p>
                  {(searchQuery || activeFilterCount > 0) && (
                    <button
                      onClick={() => { setSearchQuery(""); setSelectedFilters([]); setSelectedFormats([]); }}
                      className="text-sm text-qbd-blue font-semibold hover:underline"
                    >
                      {t("clearAllFilters")}
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {visibleResources.map((r, i) => (
                      <ResourceCard key={`${r.contentType}-${r.id}`} resource={r} index={i} />
                    ))}
                  </div>
                  <div ref={loadMoreRef} className="flex justify-center py-10">
                    {hasMore && (
                      <Loader2 className="animate-spin text-muted-foreground" size={24} />
                    )}
                    {!hasMore && filteredResources.length > 18 && (
                      <p className="text-sm text-muted-foreground">{t("reachedEnd")}</p>
                    )}
                  </div>
                </>
              )}
            </main>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Resources;
